import { CalendarDate } from '@internationalized/date';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

type CalendarDateLike = {
  toString: () => string;
};

interface YearPickerProps {
  value?: CalendarDate | null;
  onChange?: (value: CalendarDate | null) => void;
  minValue?: CalendarDateLike | null;
  maxValue?: CalendarDateLike | null;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
}

const YEARS_PER_PAGE = 12;

const parseYear = (value?: CalendarDateLike | null): number | null => {
  if (!value) return null;
  const year = Number(value.toString().split('-')[0]);
  return year || null;
};

// A single-year picker — same visual language as `MonthPicker`, used for the
// Yearly tab. Shows a paged grid of years instead of a month/quarter grid.
const YearPicker = ({
  value,
  onChange,
  minValue,
  maxValue,
  isDisabled,
  placeholder = 'Select year',
  className,
}: YearPickerProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const min = parseYear(minValue);
  const max = parseYear(maxValue);
  const selected = value ? value.year : null;

  const pageStart = (year: number) =>
    Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;

  const [viewPageStart, setViewPageStart] = useState(
    pageStart(selected ?? max ?? min ?? new Date().getFullYear())
  );

  useEffect(() => {
    if (open) {
      setViewPageStart(
        pageStart(selected ?? max ?? min ?? new Date().getFullYear())
      );
    }
    // Only re-sync the visible page when the popover opens.
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickAway = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open]);

  const years = Array.from(
    { length: YEARS_PER_PAGE },
    (_, i) => viewPageStart + i
  );

  const isYearDisabled = (year: number) => {
    if (min && year < min) return true;
    if (max && year > max) return true;
    return false;
  };

  const canGoPrevPage = !min || viewPageStart > min;
  const canGoNextPage = !max || viewPageStart + YEARS_PER_PAGE <= max;

  const label = selected ? `${selected}` : placeholder;

  const handleSelectYear = (year: number) => {
    onChange?.(new CalendarDate(year, 1, 1));
    setOpen(false);
  };

  return (
    <div className={clsx('relative', className)} ref={wrapperRef}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'group flex min-h-[46px] w-full items-center gap-3 rounded-lg border border-transparent bg-indigo-100 px-4 py-[14px] text-left shadow-xs transition-colors',
          isDisabled
            ? 'pointer-events-none opacity-60'
            : 'cursor-pointer hover:bg-indigo-500',
          open ? 'bg-indigo-100' : ''
        )}
      >
        <Calendar className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
        <span className="min-w-0 flex-1 text-sm font-medium text-indigo-800 transition-colors group-hover:text-white">
          {label}
        </span>
        <ChevronDown className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+8px)] left-0 z-50 w-[220px] overflow-hidden rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_38px_rgba(15,23,42,0.12)]">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              disabled={!canGoPrevPage}
              onClick={() => setViewPageStart((p) => p - YEARS_PER_PAGE)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-slate-800">
              {years[0]} - {years[years.length - 1]}
            </span>
            <button
              type="button"
              disabled={!canGoNextPage}
              onClick={() => setViewPageStart((p) => p + YEARS_PER_PAGE)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {years.map((year) => {
              const disabled = isYearDisabled(year);
              const isSelected = selected === year;
              return (
                <button
                  key={year}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelectYear(year)}
                  className={clsx(
                    'rounded-[10px] py-2 text-sm font-medium transition-colors',
                    disabled
                      ? 'pointer-events-none text-gray-300'
                      : isSelected
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-700 hover:bg-indigo-50'
                  )}
                >
                  {year}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default YearPicker;
